import { createRoot, hydrateRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App.tsx";
import "./index.css";
import { startServiceWorkerUpdateChecks } from "@/lib/app-cache";
import { initSentry } from "@/lib/sentry-loader";
import { scrubHealthBreadcrumb, scrubHealthEvent } from "@/lib/sentry-scrub";
import { isAppPath } from "@/lib/app-paths";
import { markHydratingPrerender } from "@/lib/hydration";
import landingI18n, { loadLandingLanguage, type LandingLang } from "@/lib/i18n-landing";
import { localeFromPath } from "@/lib/locale";

// LDT-718 · ¿Arranca la aplicación (panel, app de familias, calendario…) o la
// landing? Decide cuándo se carga Sentry y si se registra el service worker.
const IS_APP_ROUTE = isAppPath(window.location.pathname);

// LDT-718 · Sentry ya no viaja en el trozo de entrada: se carga con import()
// (src/lib/sentry-loader.ts). En la app, en cuanto arranca; en la landing,
// solo con el primer error. La configuración no cambia.
initSentry((Sentry) => Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV ?? "development",
  // LDT-105 · PROB-316: el replay (grabación de sesión) NO va aquí. Antes se
  // registraba en el arranque, así que a cada carga del sitio —antes incluso
  // de que el visitante viera el banner de cookies— quedaba listo para
  // grabar en cuanto saltara un error. Ahora se añade sólo si ya hay
  // consentimiento de "analíticas" — ver enableSentryReplayIfConsented() en
  // src/lib/sentry-replay.ts, llamada desde ConsentedAnalytics en App.tsx —
  // igual que Clarity. El resto de Sentry (errores, tracing) se mantiene
  // activo desde el arranque: es monitorización de estabilidad del sitio
  // (interés legítimo), no grabación de sesión.
  integrations: [Sentry.browserTracingIntegration()],
  // 10% de trazas en prod (antes 20%): el browser tracing instrumenta cada
  // interacción; con menos muestreo baja el overhead en el hilo principal.
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  replaysSessionSampleRate: 0.0,
  // LDT-67 · PROB-760: este 1.0 es el tope global (cuántos ERRORES intentan
  // adjuntar replay), no cuántos se graban de verdad. En rutas autenticadas
  // (/platform/*, /app/:slug — donde vive la PII de tutores y niños) el
  // `beforeErrorSampling` de src/lib/sentry-replay.ts lo reduce más, además
  // del enmascarado (`maskAllText`) que ya evita que se lea nada en el vídeo.
  replaysOnErrorSampleRate: 1.0,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  // LDT-711 · El dato de salud de una reserva (health_notes: alergias) nunca
  // sale hacia Sentry: se tapa toda clave de salud en extra, contexts, tags,
  // request.data y breadcrumbs (src/lib/sentry-scrub.ts).
  beforeSend: (event) => scrubHealthEvent(event),
  beforeBreadcrumb: (crumb) => scrubHealthBreadcrumb(crumb),
  // Filter out noise from headless crawlers (Vercel preview deploy bot,
  // lighthouse, etc.) that update a ServiceWorker mid-lifecycle and cause
  // "Failed to update a ServiceWorker for scope ('...') with script ('Unknown'):
  // Not found" rejections. Not actionable for real users.
  ignoreErrors: [
    /Failed to update a ServiceWorker/i,
    /ServiceWorker.*Not found/i,
    // React + browser auto-translation (Google Translate en Chrome Mobile sobre
    // todo): la traducción mueve nodos del DOM y luego React no los encuentra
    // al desmontar. No es accionable — silenciamos.
    /Failed to execute 'removeChild' on 'Node'/i,
    /The node to be removed is not a child of this node/i,
    /Failed to execute 'insertBefore' on 'Node'/i,
    // MISMA excepción (DOMException code 8, NotFoundError), redacción de
    // Safari/WebKit. Las tres líneas de arriba sólo cubrían la de Chrome, y por
    // eso se coló PLAYIUM-WEB-2X: iPhone con la página en español y el
    // dispositivo en ruso — es decir, traducción del navegador — reventando al
    // desmontar un <Select> del alta de familia. Lo cazó el ErrorBoundary.
    /The object can not be found here/i,
    // Chunks caducados tras un deploy: una build vieja en caché pide un chunk con
    // hash antiguo que ya no existe en el CDN. NO es un bug de código — la app se
    // auto-recarga (listener vite:preloadError + fallback del ErrorBoundary) para
    // coger la build nueva. Silenciamos el reporte para que no sea ruido en cada
    // despliegue (cubre los distintos mensajes según navegador).
    /Importing a module script failed/i,
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Failed to load module script/i,
    /dynamically imported module/i,
    /ChunkLoadError/i,
    /Loading chunk \S+ failed/i,
    // Redacción de Safari/WebKit del MISMO fallo: el catch-all de vercel.json
    // devuelve index.html (Content-Type text/html) para el chunk caducado en
    // vez de un 404, y Safari lo reporta como "'text/html' is not a valid
    // JavaScript MIME type." en vez de cualquiera de los mensajes de arriba.
    // Cubre también la variante de CSS por si Vite dispara el mismo tipo de
    // fallo al precargar el CSS de un chunk lazy. Sentry PLAYIUM-WEB-34,
    // segunda ocurrencia del hueco que ya predijo PROB-777 (la primera fue
    // PLAYIUM-WEB-1Z, 1 evento/0 usuarios; esta vez con 1 usuario afectado).
    /is not a valid .*mime type/i,
    // Igual que arriba pero para la precarga de CSS de un chunk lazy: tras un
    // deploy el <link rel=modulepreload>/CSS con hash viejo ya no existe. Vite
    // dispara vite:preloadError y auto-recargamos; silenciamos el reporte.
    /Unable to preload CSS/i,
    // Ruido de WebViews in-app de terceros (navegadores embebidos de redes
    // sociales), NO es nuestro código: inyectan puentes JS que se rompen al
    // navegar. Android (keyboard logging) e iOS (message handlers).
    /enableDidUserTypeOnKeyboardLogging/i,
    /Java object is gone/i,
    /webkit\.messageHandlers/i,
    // Instagram/Facebook en Android: su script inyectado (iabjs://) vuelca
    // telemetría al puente Java mientras la Activity ya se está destruyendo. El
    // stack no tiene un solo frame nuestro y en el repo no hay ni un postMessage
    // ni puente nativo, así que no hay nada que arreglar de nuestro lado.
    /Java exception was raised during method invocation/i,
    // Rate-limit esperado de Supabase Auth al reenviar OTP/confirmación
    // demasiado pronto. No es un bug: la UI ya muestra el aviso al usuario.
    /For security purposes, you can only request this after/i,
    /only request this after \d+ seconds/i,
    // Web Locks: auth-js roba su PROPIO lock cuando tarda más de 5s en
    // adquirirlo (iOS congela la pestaña con el refresco en vuelo). El navegador
    // rechaza la promesa del titular anterior con AbortError y el tick de
    // auto-refresh lo relanza desde un setInterval sin await. La sesión no se
    // pierde: el callback termina y la guarda igual.
    //
    // Solo este patrón. NO añadir /Acquiring an exclusive Navigator LockManager
    // lock/: ese sí indicaría contención real y taparía un auth atascado.
    /Lock was stolen by another request/i,
    // MISMA excepción (DOMException code 20, AbortError) con la redacción de
    // Chrome/Android: "Lock broken by another request with the 'steal'
    // option." en vez de "Lock was stolen by another request" — Sentry
    // PLAYIUM-WEB-30.
    /Lock broken by another request/i,
  ],
}), { onlyOnError: !IS_APP_ROUTE });

// For /app/:slug routes, we used to swap the manifest href here to a dynamic
// cross-origin manifest. However, Chrome evaluates the static <link rel=manifest>
// during HTML parsing and does NOT re-fire beforeinstallprompt when the href
// changes to a cross-origin URL. Keeping the static /manifest.json ensures
// Chrome fires beforeinstallprompt on all routes (scope: "/").
// Per-local branding (name, icon) is handled visually in PWAInstallPrompt.

// Recuperación automática de chunks caducados tras un deploy. Cuando un import()
// dinámico (ruta lazy) falla porque su chunk con hash antiguo ya no existe en el
// CDN (típico cuando un usuario tiene el index.html viejo en caché y se publica
// una build nueva), Vite dispara `vite:preloadError`. Recargamos UNA vez para
// coger la build nueva. Guard de 20s para no entrar en bucle si el fallo es real
// (p.ej. sin conexión).
//
// NO se llama a event.preventDefault(), y es deliberado. El helper __vitePreload
// termina en `return baseModule().catch(handlePreloadError)`, y handlePreloadError
// solo relanza `if (!e.defaultPrevented)`. Al cancelar el evento, ese .catch se
// tragaba el error y devolvía undefined, así que la promesa del import() RESOLVÍA
// con undefined en vez de rechazar. React.lazy guardaba entonces _result =
// undefined y al leer `_result.default` reventaba con un TypeError que ya no
// menciona ningún chunk — y por eso se colaba por el ignoreErrors de arriba.
// Eran ~50 eventos en Sentry repartidos en 6 releases (PLAYIUM-WEB-22/23/25/28)
// causados por el propio remedio.
//
// Dejando que Vite relance, llega el error REAL ("Failed to fetch dynamically
// imported module"), que sí está silenciado arriba, y la recarga se dispara
// igual porque ya se ha programado unas líneas antes.
window.addEventListener("vite:preloadError", () => {
  const KEY = "__chunk_reload_ts";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 20000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

// Capture beforeinstallprompt globally so it's not missed before React mounts
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  // `BeforeInstallPromptEvent` no está en lib.dom.d.ts; el tipo real lo
  // declara PWAInstallPrompt.tsx sobre `Window` (augmentation global).
  window.__pwaInstallPromptEvent = e as unknown as Window["__pwaInstallPromptEvent"];
});

// IMPORTANT: When the user lands on /app/:slug with a Supabase auth recovery
// hash (#access_token=...&type=recovery), we must ensure the recovery session
// is restored on the *parent* supabase client, not the admin one. Both clients
// have detectSessionInUrl=true by default, so whichever initializes first will
// consume the hash. Since AuthProvider (admin client) mounts before the parent
// portal, the admin client used to win and the parent updateUser({password})
// later failed with "Auth session missing".
// To prevent that, we capture the hash here (before any Supabase client is
// imported), stash the tokens for the parent client to consume, and clear the
// hash so the admin client cannot pick it up.
(() => {
  try {
    const path = window.location.pathname;
    const hash = window.location.hash;
    if (!path.startsWith("/app/") || !hash.includes("type=recovery")) return;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");
    if (!access_token || !refresh_token) return;
    window.__parentRecoveryTokens = { access_token, refresh_token };
    // Strip the hash so the admin Supabase client (mounted by AuthProvider)
    // does not consume it.
    history.replaceState({}, "", `${path}${window.location.search}`);
  } catch (_) {
    // ignore
  }
})();

function registerServiceWorker() {
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      if (registration) {
        const intervalId = startServiceWorkerUpdateChecks(swUrl, registration);
        window.addEventListener(
          "beforeunload",
          () => window.clearInterval(intervalId),
          { once: true },
        );
      }
    },
    onNeedRefresh() {
      void updateSW(true);
    },
    // Swallow registration errors that happen on short-lived browser sessions
    // (preview crawlers, lighthouse). The PWA simply won't be installed in those
    // contexts; real users get a normal registration.
    onRegisterError() {
      /* no-op */
    },
  });
}

async function bootstrap() {
  // LDT-718 · El service worker solo se registra en la aplicación. Su precache
  // es la app entera (~8 MB): lo que el TPV necesita para arrancar sin red
  // (PROB-762), no lo que necesita un visitante de la landing, que lo
  // descargaba en segundo plano sin haber entrado nunca al panel. Quien usa el
  // TPV pasa siempre por /platform/login o abre la PWA instalada (start_url en
  // /platform o /app), y ahí se registra como hasta ahora. Un navegador que ya
  // lo tenía de antes lo conserva: registrarlo o no desde la landing no lo quita.
  if (IS_APP_ROUTE) registerServiceWorker();

  // LDT-718 · Los textos en catalán e inglés van en su propio trozo: se cargan
  // antes de montar. En la landing manda la URL, y se fija ya (el banner de
  // cookies se pinta antes que <LangLayout>, igual que en el prerender); en la
  // app, el idioma guardado, que es el que usa el banner.
  const lang = IS_APP_ROUTE
    ? (landingI18n.language as LandingLang)
    : localeFromPath(window.location.pathname);
  try {
    await loadLandingLanguage(lang);
    if (!IS_APP_ROUTE) await landingI18n.changeLanguage(lang);
  } catch {
    // Sin el trozo del idioma (red caída), se pinta con el castellano de
    // respaldo antes que no pintar nada.
  }

  // LDT-718 · Las páginas de la landing llegan prerenderizadas: React se
  // engancha a ese HTML (hydrateRoot) en vez de tirarlo y repintarlo. app.html
  // y 404.html traen el #root vacío y se montan desde cero, como antes.
  const root = document.getElementById("root")!;
  if (root.hasChildNodes()) {
    markHydratingPrerender();
    hydrateRoot(root, <App />);
  } else {
    createRoot(root).render(<App />);
  }
}

void bootstrap();
